import React, { useMemo } from 'react';
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { useControllerExceptionRate } from '../../../hooks/analytics/useControllerReports.ts';
import { formatNumber, WidgetCard } from './shared.tsx';
import { CHART_AXIS_LINE, CHART_AXIS_TICK, CHART_CATEGORICAL, CHART_GRID_STROKE, SEMANTIC } from '../theme.ts';
import type { ControllerExceptionRateRow } from '../../../request/analytics/controllerReports.ts';

const TOLERABLE_DEVIATION_PCT = 5;

type ScatterPoint = {
  control_id: string;
  title: string;
  sample: number;
  deviationPct: number;
  /** Bubble size; 0 when the control has no population (renders at the minimum size). */
  pop: number;
  /** Real population, or null when none applies (e.g. tested from evidence files). */
  popValue: number | null;
  total_exceptions: number;
};

const ChartTooltip: React.FC<{ active?: boolean; payload?: { payload: ScatterPoint }[] }> = ({
  active,
  payload,
}) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-lg">
      <p className="font-semibold text-slate-900">
        {point.control_id} — {point.title}
      </p>
      <p className="mt-1 text-slate-500">Sample size: {formatNumber(point.sample)}</p>
      <p className="text-slate-500">Avg deviation: {point.deviationPct.toFixed(1)}%</p>
      <p className="text-slate-500">Population: {formatNumber(point.popValue)}</p>
      <p className="text-slate-500">Exceptions: {formatNumber(point.total_exceptions)}</p>
    </div>
  );
};

/**
 * Report #22 — Exception Rate by Control. Scatter chart (sample size vs.
 * avg deviation rate, bubble sized by population) with a reference line at
 * the 5% tolerable deviation threshold; a compact table lists the worst
 * offenders below it.
 */
export const ExceptionRateChart: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useControllerExceptionRate();
  const rows = useMemo(() => {
    const base = data ?? [];
    return controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
  }, [data, controlIdFilter]);

  const points = useMemo<ScatterPoint[]>(
    () =>
      rows
        .filter((row): row is ControllerExceptionRateRow & { sample: number } => row.sample != null)
        .map((row) => ({
          control_id: row.control_id,
          title: row.title,
          sample: row.sample,
          deviationPct: (row.avg_deviation ?? 0) * 100,
          pop: row.pop ?? 0,
          popValue: row.pop ?? null,
          total_exceptions: row.total_exceptions ?? 0,
        })),
    [rows]
  );

  const worst = useMemo(() => [...rows].sort((a, b) => (b.avg_deviation ?? 0) - (a.avg_deviation ?? 0)).slice(0, 5), [rows]);

  return (
    <WidgetCard
      title="Exception Rate by Control"
      description="Sample size vs. average deviation rate; bubble size = population"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load exception rate data.'}
      isEmpty={!isLoading && !isError && points.length === 0}
      emptyMessage="No tested controls with sample data yet."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis
              type="number"
              dataKey="sample"
              name="Sample Size"
              tick={CHART_AXIS_TICK}
              axisLine={CHART_AXIS_LINE}
              tickLine={false}
            />
            <YAxis
              type="number"
              dataKey="deviationPct"
              name="Deviation Rate"
              unit="%"
              tick={CHART_AXIS_TICK}
              axisLine={CHART_AXIS_LINE}
              tickLine={false}
            />
            <ZAxis type="number" dataKey="pop" range={[40, 400]} name="Population" />
            <Tooltip content={<ChartTooltip />} cursor={{ strokeDasharray: '3 3', stroke: CHART_GRID_STROKE }} />
            <ReferenceLine
              y={TOLERABLE_DEVIATION_PCT}
              stroke={SEMANTIC.critical.chart}
              strokeDasharray="4 4"
              label={{ value: 'Tolerable (5%)', position: 'right', fill: SEMANTIC.critical.chart, fontSize: 11 }}
            />
            <Scatter data={points} fill={CHART_CATEGORICAL[0]} fillOpacity={0.7} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      {worst.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Highest deviation</p>
          <ul className="text-sm divide-y divide-gray-100">
            {worst.map((row) => (
              <li key={row.control_id} className="flex items-center justify-between py-1.5">
                <span className="text-gray-700 truncate">
                  {row.control_id} — {row.title}
                </span>
                <span
                  className={`ml-3 shrink-0 font-medium ${
                    (row.avg_deviation ?? 0) * 100 > TOLERABLE_DEVIATION_PCT ? 'text-red-600' : 'text-gray-600'
                  }`}
                >
                  {row.avg_deviation != null ? `${(row.avg_deviation * 100).toFixed(1)}%` : '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </WidgetCard>
  );
};

export default ExceptionRateChart;
